import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Divider,
  Stack,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MermaidDiagram from '../components/MermaidDiagram';
import { SYSTEM_FLOWS, getFlowSections, getFlowByKey } from './system-flows/flows-content';

/**
 * "How It Works" — a Guides-style page of plain-English flow diagrams that
 * explain how parts of the system behave, so the team can understand the logic
 * without reading code. A topic sidebar (grouped by section) on the left; the
 * selected diagram + its summary on the right.
 *
 * Internal documentation — shown on BOTH the IA and broker-admin builds (not
 * gated in nav-config). Content lives in ./system-flows/flows-content.ts.
 */
const SystemFlowsPage: React.FC = () => {
  const sections = getFlowSections();
  const navigate = useNavigate();
  // The selected topic is driven by the URL (/system-flows/:flowKey) so every
  // topic is deep-linkable + shareable. An unknown/absent key falls back to the
  // first flow. Clicking a topic navigates (appends the key to the path).
  const { flowKey } = useParams<{ flowKey?: string }>();
  const active = (flowKey ? getFlowByKey(flowKey) : undefined) ?? SYSTEM_FLOWS[0];
  const activeKey = active?.key ?? '';

  return (
    <Box>
      {/* Page header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <AccountTreeIcon color="primary" />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          How It Works
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Plain-English flow diagrams explaining how different parts of the system work — so the
        team can understand the logic without reading the code.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Topic sidebar */}
        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', md: 280 },
            flexShrink: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            alignSelf: 'flex-start',
            overflow: 'hidden',
          }}
        >
          <List disablePadding>
            {sections.map(({ section, flows }) => (
              <li key={section}>
                <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                  <ListSubheader
                    sx={{
                      bgcolor: 'background.default',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      letterSpacing: '0.05em',
                      color: 'text.secondary',
                      lineHeight: '36px',
                    }}
                  >
                    {section}
                  </ListSubheader>
                  {flows.map((flow) => (
                    <ListItemButton
                      key={flow.key}
                      selected={flow.key === activeKey}
                      onClick={() => navigate(`/system-flows/${flow.key}`)}
                      sx={{ pl: 3 }}
                    >
                      <ListItemText
                        primary={flow.title}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: flow.key === activeKey ? 600 : 400,
                        }}
                      />
                    </ListItemButton>
                  ))}
                </ul>
              </li>
            ))}
          </List>
        </Paper>

        {/* Selected diagram */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minWidth: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: { xs: 2.5, md: 4 },
          }}
        >
          {active && (
            <>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                {active.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {active.summary}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <MermaidDiagram code={active.mermaid} />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default SystemFlowsPage;
